'use client';

import InstallAppSection from '@frontend/app/shared/layout/InstallAppSection';
import PaymentPreferencesSection from '@frontend/app/shared/layout/PaymentPreferencesSection';
import { useUserSettingsStore } from '@frontend/client/contexts/useUserSettingsStore';
import { useSignedPreferences } from '@frontend/client/hooks/useSignedPreferences';
import { truncateAddress } from '@frontend/common';
import ResponsiveModal from '@frontend/components/ResponsiveModal';
import UserAvatar from '@frontend/components/UserAvatar';
import { WalletManagementModal } from '@frontend/components/wallet';
import { isStandalone } from '@frontend/utils/isMobile';
import { MOBILE_QUERY, useMediaQuery } from '@frontend/utils/UseMediaQuery';
import { usePrimaryName } from '@justaname.id/react';
import {
  ChevronRightIcon,
  ExternalLinkIcon,
  GlobeIcon,
  LockClosedIcon,
  MoonIcon,
  PlusIcon,
  SunIcon,
} from '@radix-ui/react-icons';
import {
  Card,
  Flex,
  Grid,
  SegmentedControl,
  Separator,
  Text,
} from '@radix-ui/themes';
import { useTheme } from 'next-themes';
import Link from 'next/link';
import { FC, useState } from 'react';
import { useAccount } from 'wagmi';

type Sections = 'main' | 'paymentPreferences' | 'installApp';

const UserSettingsSection: FC = () => {
  const { address } = useAccount();
  const { preferredPrivacy, setPreferredPrivacy } = useUserSettingsStore();
  const { theme, setTheme } = useTheme();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  const isInstallable = isMobile && !isStandalone;

  const [currentSection, setCurrentSection] = useState<Sections>('main');
  const [showPreferences, setShowPreferences] = useState(false);
  const [showWallets, setShowWallets] = useState(false);

  // Get primary name and preferences for PaymentPreferencesSection
  const { primaryName } = usePrimaryName({ address });
  const { preferences: existingPreferences, isLoading: isPreferencesLoading } =
    useSignedPreferences({ addressOrEns: address });

  return (
    <Grid gap="4">
      {currentSection === 'main' && (
        <>
          <Card
            hidden={currentSection !== 'main'}
            variant="classic"
            size="2"
            className="cursor-pointer"
            onClick={() => {
              setShowWallets(true);
            }}
          >
            <Flex justify="between" align="center">
              <Flex gap="3" align="center">
                <UserAvatar addressOrEns={address} />
                <Flex direction="column">
                  {(address || primaryName) && (
                    <Text size="2" weight="bold">
                      {primaryName || truncateAddress(address || '', 4)}
                    </Text>
                  )}
                  {primaryName && (
                    <Text size="1" color="gray">
                      {truncateAddress(address || '', 4)}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Card>

          <Separator orientation="horizontal" size="4" className="opacity-20" />

          <Flex direction="column" gap="2">
            {primaryName && (
              <Link
                href={`https://app.justaname.id/?ens=${primaryName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Card variant="classic" size="2" className="cursor-pointer">
                  <Flex justify="between" align="center">
                    <div>
                      <Text size="2" weight="bold" as="p">
                        Profile
                      </Text>
                      <Text color="gray" size="1" as="p">
                        Manage your ENS profile
                      </Text>
                    </div>
                    <ExternalLinkIcon />
                  </Flex>
                </Card>
              </Link>
            )}

            <Card
              className="cursor-pointer"
              variant="classic"
              size="2"
              onClick={() => {
                setShowPreferences(true);
              }}
            >
              <Flex justify="between" align="center">
                <div>
                  <Text size="2" weight="bold" as="p">
                    Payment Setup
                  </Text>
                  <Text color="gray" size="1" as="p">
                    Set receiving tokens and chains
                  </Text>
                </div>
                <ChevronRightIcon />
              </Flex>
            </Card>
          </Flex>

          {isInstallable && (
            <>
              <Separator
                orientation="horizontal"
                size="4"
                className="opacity-20"
              />

              <Flex direction="column" gap="2">
                <Card
                  onClick={() => {
                    setCurrentSection('installApp');
                  }}
                  variant="classic"
                  size="2"
                  className="cursor-pointer bg-gradient-to-r from-transparent to-[--accent-3]"
                >
                  <Flex justify="between" align="center">
                    <div>
                      <Text size="2" weight="bold" as="p">
                        Install App
                      </Text>
                      <Text color="gray" size="1" as="p">
                        Add yodl to your home screen
                      </Text>
                    </div>
                    <PlusIcon />
                  </Flex>
                </Card>
              </Flex>
            </>
          )}

          {/* Settings */}
          <Separator orientation="horizontal" size="4" className="opacity-20" />
          <Flex direction="column" gap="4">
            <Flex justify="between" align="center">
              <div>
                <Text size="2" weight="bold" as="p">
                  Hide Balances
                </Text>
              </div>
              <SegmentedControl.Root defaultValue={preferredPrivacy}>
                <SegmentedControl.Item
                  value="public"
                  onClick={() => setPreferredPrivacy('public')}
                >
                  <GlobeIcon />
                </SegmentedControl.Item>
                <SegmentedControl.Item
                  value="private"
                  onClick={() => setPreferredPrivacy('private')}
                >
                  <LockClosedIcon />
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </Flex>

            <Flex justify="between" align="center">
              <div>
                <Text size="2" weight="bold" as="p">
                  Theme
                </Text>
              </div>
              <SegmentedControl.Root defaultValue={theme}>
                <SegmentedControl.Item
                  value="light"
                  onClick={() => setTheme('light')}
                >
                  <SunIcon />
                </SegmentedControl.Item>
                <SegmentedControl.Item
                  value="dark"
                  onClick={() => setTheme('dark')}
                >
                  <MoonIcon />
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </Flex>
          </Flex>
        </>
      )}

      <ResponsiveModal
        open={showPreferences}
        onOpenChange={setShowPreferences}
        mobileVariant="floatingIsland"
        desktopVariant="dialog"
      >
        <PaymentPreferencesSection
          primaryName={primaryName}
          existingPreferences={existingPreferences}
          isPreferencesLoading={isPreferencesLoading}
        />
      </ResponsiveModal>

      <WalletManagementModal open={showWallets} onOpenChange={setShowWallets} />

      {currentSection === 'installApp' && <InstallAppSection />}
    </Grid>
  );
};

export default UserSettingsSection;
